/* eslint-disable prettier/prettier */

// Library Imports
import * as React from "react";
import {
  Grid,
  Link,
  Stack,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  capitalize,
  TableCell,
  Tooltip,
  IconButton,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CreateIcon from "@mui/icons-material/Create";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { Delete } from "@mui/icons-material";
import { useParams } from "react-router-dom";

// component imports
import FlashMessage from "../../../shared/FlashMessage";

// app setting imports
import { AuthenticationContext } from "../../../Context";

// importing header helpers
import { getHeaders } from "../../../utils/HeaderHelper";
import {
  snakeCaseToTitleCase,
  toTitleCase,
} from "../../../utils/CaseFormatHelper";
import NewEncounter from "../../../modals/NewEncounter";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import EncounterHistory from "./EncounterHistory";
import Modal from "../../../modals/Modal";
import { useFlash } from "../../../FlashContext";

interface Props {}

const modalContent = (
  <div className="modal-content">
    <p className="align-center">
      You are attempting to delete this encounter. This action cannot be undone.
      Would you like to continue?
    </p>
  </div>
);

const PatientEncounterSummary: React.FC<Props> = (props: any) => {
  // authentication context
  const authenticationSetting = React.useContext(AuthenticationContext);

  const { showToast } = useFlash();
  let { id: patientId } = useParams();
  const [allEncounters, setAllEncounters] = React.useState<any>();
  const [openEncounterModal, setOpenEncounterModal] = React.useState(false);
  const [showEncounterHistory, setShowEncounterHistory] =
    React.useState<boolean>(false);
  const [showEncounterDeleteModal, setShowEncounterDeleteModal] =
    React.useState<boolean>(false);
  const [selectedEncounterBillingLogs, setSelectedEncounterBillingLogs] =
    React.useState([]);
  const [selectedEncounter, setSelectedEncounter] = React.useState<any>(null);

  React.useEffect(() => {
    getEncounterData();
  }, [patientId]);

  const getEncounterData = () => {
    if (patientId) {
      fetch(`/encounters/list/${patientId}`, {
        method: "GET",
        headers: getHeaders(authenticationSetting.csrfToken),
      })
        .then((result) => result.json())
        .then((result) => {
          if (result.success == false) {
            showToast({ 
              title: "Encounter Data Load Error", 
              text: result.error || "Failed to retrieve encounter data.", 
              type: "error" 
            });
          } else {
            setAllEncounters(result?.resource);
          }
        })
        .catch((error) => {
          showToast({ 
            title: "Encounter Data Load Error", 
            text: error.message || "Failed to retrieve encounter data.", 
            type: "error" 
          });
        });
    }
  };

  const renderStatus = (status: string) => {
    let tagContainer = "label-tag-container";
    let tagLabel = "label-tag";

    switch (status?.toLowerCase()) {
      case "draft":
        tagContainer = "label-tag-container tertiary-container";
        tagLabel = "label-tag tertiary-tag";
        break;
      case "pending submission":
        tagContainer = "label-tag-container secondary-container";
        tagLabel = "label-tag secondary-tag";
        break;
      case "submitted":
        tagContainer = "label-tag-container primary-container";
        tagLabel = "label-tag primary-tag";
        break;
    }

    return (
      <div className={tagContainer} style={{ marginLeft: 0, marginBottom: 10 }}>
        <p className={tagLabel}>{snakeCaseToTitleCase(status)}</p>
      </div>
    );
  };

  const handleShowEncounterHistory = (encounter) => {
    setSelectedEncounterBillingLogs(encounter?.encounter_billing_loggers);
    openEncounterHistoryModal();
  };

  const openEncounterHistoryModal = () => {
    setShowEncounterHistory(true);
  };

  const closeEncounterHistoryModal = () => {
    setSelectedEncounterBillingLogs([]);
    setShowEncounterHistory(false);
  };

  const openEncounterDeleteModal = (encounter) => {
    setSelectedEncounter(encounter);
    setShowEncounterDeleteModal(true);
  };

  const closeEncounterDeleteModal = () => {
    setSelectedEncounter(null);
    setShowEncounterDeleteModal(false);
  };

  const handleEncounterDelete = () => {
    if (selectedEncounter?.id) {
      fetch(`/encounter/${selectedEncounter.id}`, {
        method: "DELETE",
        headers: getHeaders(authenticationSetting.csrfToken),
      })
        .then((result) => result.json())
        .then((result) => {
          if (result.success == false) {
            console.log(result.error);
            showToast({ title: "Error Deleting Encounter", text: result.error, type: "error" });
          } else {
            showToast({ title: "Encounter Deleted Successfully", text: "Encounter deleted successfully", type: "success" });
            getEncounterData();
          }
        })
        .catch((error) => {
          console.log(error);
          showToast({ title: "Failed to Delete Encounter", text: "Failed to delete encounter", type: "error" });
        })
        .finally(() => {
          closeEncounterDeleteModal();
        });
    }
  };

  return (
    <Grid container className="panel-container">
      <Grid item xs={12}>
        <Grid container className="panel-show-container">
          <Grid container className="panel-information-container">
            <Grid container direction="row" className="admin-header">
              <Grid item xs={12} className="box-header">
                <Stack
                  direction={"row"}
                  justifyContent={"space-between"}
                  paddingX={3}
                  paddingY={1}
                  alignItems={"center"}
                  display={"flex"}
                >
                  <Grid item>
                    <h3>Encounters Summary</h3>
                  </Grid>
                  <Link
                    className="action-link add-encounter"
                    onClick={() => setOpenEncounterModal(true)}
                  >
                    <AddIcon className="plus-icon" />{" "}
                    <span className="app-user-text" style={{ marginLeft: "0" }}>
                      New Encounter
                    </span>
                  </Link>
                </Stack>
              </Grid>
            </Grid>
            <Grid
              container
              className="grey-container"
              style={{
                marginTop: "-11px",
              }}
            >
              <Grid container className="tabs-container">
                <Grid
                  item
                  xs={12}
                  className="panel-body tab-body-container"
                  padding={0}
                  mt={1}
                  sx={{ border: "0px !important" }}
                >
                  <TableContainer>
                    <Table>
                      <TableHead className="table-header-box">
                        <TableRow>
                          <TableCell sx={{ fontFamily: "QuicksandBold" }}>
                            Date
                          </TableCell>
                          <TableCell sx={{ fontFamily: "QuicksandBold" }}>
                            Type
                          </TableCell>
                          <TableCell sx={{ fontFamily: "QuicksandBold" }}>
                            Encounter Status
                          </TableCell>
                          <TableCell
                            align="center"
                            width={"17%"}
                            sx={{ fontFamily: "QuicksandBold" }}
                          >
                            Most Recent Claim Status
                          </TableCell>
                          <TableCell
                            align="center"
                            width={"5%"}
                            sx={{ fontFamily: "QuicksandBold" }}
                          >
                            History
                          </TableCell>
                          <TableCell
                            align="center"
                            width={"5%"}
                            sx={{ fontFamily: "QuicksandBold" }}
                          >
                            View/Edit
                          </TableCell>
                          <TableCell
                            align="center"
                            width={"5%"}
                            sx={{ fontFamily: "QuicksandBold" }}
                          >
                            Delete
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {allEncounters?.map((encounter) => (
                          <TableRow
                            key={encounter?.id}
                            sx={{ "& td, & th": { borderBottom: "none" } }}
                          >
                            <TableCell className="date-data">
                              {encounter.formatted_date}
                            </TableCell>
                            <TableCell>
                              {encounter?.encounter_type ===
                              "diabetes_management"
                                ? "NDPERS Diabetes Program"
                                : snakeCaseToTitleCase(
                                    encounter?.encounter_type
                                  )}
                            </TableCell>
                            <TableCell>
                              {toTitleCase(encounter?.status)}
                            </TableCell>
                            <TableCell align="center">
                              {encounter?.most_recent_claim_status
                                ? renderStatus(
                                    encounter?.most_recent_claim_status
                                  )
                                : "N/A"}
                            </TableCell>
                            <TableCell align="center" width={"5%"}>
                              {encounter.encounter_billing_loggers.length >
                              0 ? (
                                <AccessTimeOutlinedIcon
                                  style={{
                                    color: "black",
                                    cursor: "pointer",
                                  }}
                                  onClick={() =>
                                    handleShowEncounterHistory(encounter)
                                  }
                                />
                              ) : (
                                <Tooltip
                                  title="Encounter does not have any history."
                                  arrow
                                  componentsProps={{
                                    tooltip: {
                                      sx: {
                                        backgroundColor: "#EFE9E8",
                                        color: "black",
                                      },
                                    },
                                    arrow: {
                                      sx: {
                                        color: "#EFE9E8",
                                      },
                                    },
                                  }}
                                >
                                  <AccessTimeOutlinedIcon
                                    style={{
                                      color: "gray",
                                      cursor: "pointer",
                                    }}
                                  />
                                </Tooltip>
                              )}
                            </TableCell>
                            <TableCell align="center" width={"5%"}>
                              {encounter.status == "pended" ? (
                                <a
                                  title="Edit Note"
                                  href={`/patient_reports/${patientId}/patient_encounters/new?encounter_id=${encounter.uuid}`}
                                >
                                  <CreateIcon sx={{ color: "black" }} />
                                </a>
                              ) : (
                                <a
                                  title="View Note"
                                  href={`/patient_reports/${patientId}/patient_encounters/new?encounter_id=${encounter.uuid}&view_only=1`}
                                >
                                  <VisibilityIcon sx={{ color: "black" }} />
                                </a>
                              )}
                            </TableCell>
                            <TableCell align="center">
                              <IconButton
                                onClick={() =>
                                  openEncounterDeleteModal(encounter)
                                }
                                disabled={encounter.status == "signed"}
                              >
                                <Delete />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      {openEncounterModal && (
        <NewEncounter
          modalOpen={openEncounterModal}
          setModalOpen={setOpenEncounterModal}
        />
      )}

      {showEncounterHistory && (
        <EncounterHistory
          selectedEncounterBillingLogs={selectedEncounterBillingLogs}
          showEncounterHistory={showEncounterHistory}
          closeEncounterHistoryModal={closeEncounterHistoryModal}
        />
      )}

      <Modal
        successModalOpen={showEncounterDeleteModal}
        setSuccessModalOpen={closeEncounterDeleteModal}
        successHeader="Delete Encounter"
        successContent={modalContent}
        successCallback={handleEncounterDelete}
        closeCallback={closeEncounterDeleteModal}
        confirmButtonText="Continue"
      />
    </Grid>
  );
};

export default PatientEncounterSummary;
